import { useState, useEffect } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { Menu, X, Download, Globe } from "lucide-react";
import { BRAND, NAV_LINKS } from "@/data/content";
import { useLang } from "@/i18n/LanguageContext";

export const Header = () => {
    const [scrolled, setScrolled] = useState(false);
    const [open, setOpen] = useState(false);
    const { t, lang, toggle } = useLang();
    const location = useLocation();
    const navigate = useNavigate();
    const onHome = location.pathname === "/";

    useEffect(() => {
        const onScroll = () => setScrolled(window.scrollY > 30);
        window.addEventListener("scroll", onScroll);
        return () => window.removeEventListener("scroll", onScroll);
    }, []);

    // Smoothly handle in-page anchors whether on home or another route
    const goAnchor = (hash) => (e) => {
        if (!hash) return;
        if (onHome) {
            e.preventDefault();
            const el = document.querySelector(hash);
            if (el) el.scrollIntoView({ behavior: "smooth", block: "start" });
            setOpen(false);
        } else {
            e.preventDefault();
            navigate(`/${hash}`);
            setOpen(false);
        }
    };

    return (
        <header
            data-testid="site-header"
            className={`fixed top-0 inset-x-0 z-50 transition-all duration-300 ${
                scrolled
                    ? "bg-black/70 backdrop-blur-xl border-b border-white/5"
                    : "bg-transparent"
            }`}
        >
            <div className="max-w-7xl mx-auto px-5 sm:px-8 h-14 sm:h-16 flex items-center justify-between">
                <Link to="/" data-testid="header-logo" className="flex items-center group">
                    <img
                        src={BRAND.logo}
                        alt="606FITNESS"
                        className="h-24 sm:h-32 lg:h-36 w-auto object-contain transition-transform group-hover:scale-105"
                    />
                </Link>

                <nav className="hidden md:flex items-center gap-8">
                    {NAV_LINKS.map((l) => (
                        <a
                            key={l.href}
                            href={`/${l.href}`}
                            onClick={goAnchor(l.href)}
                            data-testid={`nav-${l.href.replace("#", "")}`}
                            className="text-sm text-neutral-300 hover:text-[var(--primary)] transition-colors relative group"
                        >
                            {t.nav[l.key]}
                            <span className="absolute -bottom-1 inset-x-0 w-0 h-[2px] bg-[var(--primary)] transition-all duration-300 group-hover:w-full" />
                        </a>
                    ))}
                </nav>

                <div className="flex items-center gap-2">
                    <button
                        onClick={toggle}
                        data-testid="lang-toggle"
                        aria-label="Switch language"
                        className="hidden md:inline-flex items-center gap-1.5 h-11 px-4 rounded-full border border-white/15 text-neutral-200 text-xs font-bold tracking-widest hover:border-[var(--primary)] hover:text-[var(--primary)] transition-all"
                    >
                        <Globe className="w-3.5 h-3.5" />
                        {t.switchTo}
                    </button>

                    <a
                        href="/#download"
                        onClick={goAnchor("#download")}
                        data-testid="header-download-cta"
                        className="hidden md:inline-flex btn-primary items-center gap-2 h-11 px-5 rounded-full text-sm"
                    >
                        <Download className="w-4 h-4" />
                        {t.header.cta}
                    </a>

                    <button
                        data-testid="header-mobile-toggle"
                        onClick={() => setOpen((v) => !v)}
                        className="md:hidden text-white p-2"
                        aria-label="Toggle menu"
                    >
                        {open ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
                    </button>
                </div>
            </div>

            {open && (
                <div
                    data-testid="mobile-menu"
                    className="md:hidden bg-black/95 backdrop-blur-xl border-t border-white/5"
                >
                    <div className="px-5 py-5 flex flex-col gap-4">
                        {NAV_LINKS.map((l) => (
                            <a
                                key={l.href}
                                href={`/${l.href}`}
                                onClick={goAnchor(l.href)}
                                className="text-neutral-200 py-2 border-b border-white/5"
                            >
                                {t.nav[l.key]}
                            </a>
                        ))}
                        <button
                            onClick={() => {
                                toggle();
                                setOpen(false);
                            }}
                            data-testid="lang-toggle-mobile"
                            className="flex items-center gap-2 py-2 border-b border-white/5 text-neutral-200"
                        >
                            <Globe className="w-4 h-4" />
                            {lang === "ar" ? "English" : "العربية"}
                        </button>
                        <a
                            href="/#download"
                            onClick={goAnchor("#download")}
                            className="btn-primary px-5 py-3 rounded-full text-center"
                        >
                            {t.header.cta}
                        </a>
                    </div>
                </div>
            )}
        </header>
    );
};
